import React, { useEffect, useMemo } from "react";
import { Dialog, Stack, Typography } from "@mui/material";
import { useForm, FormProvider, useWatch } from "react-hook-form";

import {
  HubButton,
  HubHookCustomersSelect,
  HubHookFormInput,
  HubHookFormPartSelectV2,
  HubHookFormSelect,
  StationHookFormSelect,
  VendorHookFormSelect,
} from "..";
import {
  FILE_CATEGORY_LABELS,
  META_FIELD_LABELS,
  getCategoryLabel,
  getCategoryRequirements,
} from "../../constants/attachments";

const AttachmentMetaDialog = ({
  open,
  category,
  categoryOptions,
  fileName,
  defaults = {},
  onCancel,
  onConfirm,
}) => {
  const showCategorySelect = !!categoryOptions?.length;

  const categorySelectOptions = useMemo(() => {
    if (!showCategorySelect) return [];
    return FILE_CATEGORY_LABELS.filter((c) =>
      categoryOptions.includes(c.id),
    ).map(({ id, label }) => ({ id, label }));
  }, [categoryOptions, showCategorySelect]);

  const initialCategory =
    category ?? (showCategorySelect ? categorySelectOptions[0]?.id : undefined);

  const methods = useForm({
    defaultValues: { category: initialCategory, ...defaults },
  });
  const { control, handleSubmit, reset } = methods;

  const selectedCategory = useWatch({ control, name: "category" });
  const effectiveCategory = selectedCategory ?? initialCategory;

  const requirements = useMemo(
    () => getCategoryRequirements(effectiveCategory),
    [effectiveCategory],
  );

  const isProvided = (field) => {
    const v = defaults?.[field];
    return v != null && v !== "";
  };
  const shows = (field) => requirements.includes(field) && !isProvided(field);

  const categoryMeta = useMemo(
    () => getCategoryLabel(effectiveCategory),
    [effectiveCategory],
  );

  useEffect(() => {
    if (open) reset({ category: initialCategory, ...defaults });
  }, [open]);

  const onSubmit = (values) => {
    const payload = { category: Number(values.category ?? initialCategory) };
    requirements.filter(shows).forEach((field) => {
      const raw = values[field];
      if (raw == null || raw === "") {
        payload[field] = null;
        return;
      }
      payload[field] = Number(raw);
    });
    onConfirm(payload);
  };

  const dialogTitle = showCategorySelect
    ? "Categorize Attachment"
    : `${categoryMeta?.label || "Attachment"} Details`;

  const submitDisabled = effectiveCategory == null || effectiveCategory === "";

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <FormProvider {...methods}>
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          gap="20px"
          padding="25px"
        >
          <Stack alignItems="center" gap="4px">
            <Typography variant="h6">{dialogTitle}</Typography>
            <Typography variant="caption" color="text.secondary">
              Provide a few details for{" "}
              <strong>{fileName || "this file"}</strong> before uploading.
            </Typography>
          </Stack>

          <Stack gap="12px">
            {showCategorySelect && (
              <HubHookFormSelect
                name="category"
                label="File Type"
                options={categorySelectOptions}
                defaultValue={initialCategory}
                required
              />
            )}
            {shows("customerId") && (
              <HubHookCustomersSelect
                name="customerId"
                label={META_FIELD_LABELS.customerId}
                required
              />
            )}
            {shows("partId") && (
              <HubHookFormPartSelectV2
                name="partId"
                label={META_FIELD_LABELS.partId}
                required={false}
              />
            )}
            {shows("vendorId") && (
              <VendorHookFormSelect
                name="vendorId"
                label={META_FIELD_LABELS.vendorId}
                required
              />
            )}
            {shows("stationId") && (
              <StationHookFormSelect
                name="stationId"
                label={META_FIELD_LABELS.stationId}
                department={6}
                required
              />
            )}
            {shows("price") && (
              <HubHookFormInput
                name="price"
                label={META_FIELD_LABELS.price}
                placeholder="0.00"
                isCurrency
                required
              />
            )}
          </Stack>

          <Stack
            direction="row"
            justifyContent="center"
            gap="10px"
            paddingTop="5px"
          >
            <HubButton
              variant="contained"
              color="grey"
              size="small"
              label="Cancel"
              onClick={onCancel}
              sx={{ minWidth: 120 }}
            />
            <HubButton
              variant="contained"
              type="submit"
              size="small"
              label="Upload"
              disabled={submitDisabled}
              sx={{ minWidth: 120 }}
            />
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  );
};

export default AttachmentMetaDialog;
